import React, { useState } from "react";
import {
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import Card from "../../../componentLibrary/Card";
import { ErrorReportI } from "../../../api/types";
import { useUpdateErrorReport } from "../../../api/queries";

interface ErrorReportCardProps {
  error: ErrorReportI;
}

const STACK_DELIMITER = "\n\nStack:\n";

const ErrorReportCard: React.FC<ErrorReportCardProps> = ({ error }) => {
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [showStack, setShowStack] = useState(false);
  const updateErrorReport = useUpdateErrorReport();

  const hasStack = error.message.includes(STACK_DELIMITER);
  const [messagePart, stackPart] = hasStack
    ? error.message.split(STACK_DELIMITER)
    : [error.message, null];
  const copyTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (statusCode: number) => {
    if (statusCode >= 500) return "text-red-600";
    if (statusCode >= 400) return "text-orange-600";
    return "text-yellow-600";
  };

  const handleCopy = async (text: string) => {
    try {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      await navigator.clipboard.writeText(text);
      setCopiedUrl(text);
      copyTimeoutRef.current = setTimeout(() => {
        setCopiedUrl(null);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      // console.error("Failed to copy text:", err);
    }
  };

  const handleResolvedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateErrorReport.mutate({
      id: error._id,
      resolved: e.target.checked,
    });
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <div className="space-y-3">
        {/* Header with status code and resolved checkbox */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon
              className={`w-6 h-6 ${getStatusColor(error.statusCode)}`}
            />
            <div>
              <span
                className={`text-lg font-bold ${getStatusColor(error.statusCode)}`}
              >
                {error.statusCode}
              </span>
              <span className="text-sm text-gray-600 ml-2">
                {error.statusText}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={error.resolved || false}
                onChange={handleResolvedChange}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <span className="text-sm text-gray-700">Resolved</span>
            </label>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <ClockIcon className="w-4 h-4" />
              <span>{formatDate(error.timestamp)}</span>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <div className="bg-gray-50 p-3 rounded-md space-y-2">
          <p className="text-sm font-medium text-gray-800 break-words">
            {messagePart}
          </p>
          {hasStack && (
            <>
              <button
                type="button"
                onClick={() => setShowStack((prev) => !prev)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                {showStack ? (
                  <>
                    <ChevronUpIcon className="w-4 h-4" />
                    Hide stack trace
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="w-4 h-4" />
                    Show stack trace
                  </>
                )}
              </button>
              {showStack && stackPart && (
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                  {stackPart}
                </pre>
              )}
            </>
          )}
        </div>

        {/* URL */}
        <div className="flex items-start gap-2">
          <button
            onClick={() => handleCopy(error.url)}
            className="flex-shrink-0 mt-0.5 cursor-pointer hover:bg-gray-100 rounded p-1 transition-colors"
            title="Copy URL"
          >
            {copiedUrl === error.url ? (
              <CheckIcon className="w-4 h-4 text-green-600" />
            ) : (
              <ClipboardDocumentIcon className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-600 font-semibold mb-1">
              Request URL:
            </p>
            <a
              href={error.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline break-all"
            >
              {error.url}
            </a>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <button
            onClick={() => handleCopy(error.pageUrl)}
            className="flex-shrink-0 mt-0.5 cursor-pointer hover:bg-gray-100 rounded p-1 transition-colors"
            title="Copy URL"
          >
            {copiedUrl === error.pageUrl ? (
              <CheckIcon className="w-4 h-4 text-green-600" />
            ) : (
              <ClipboardDocumentIcon className="w-4 h-4 text-gray-500" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-600 font-semibold mb-1">
              Page URL:
            </p>
            <a
              href={error.pageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline break-all"
            >
              {error.pageUrl}
            </a>
          </div>
        </div>

        {/* User Description (if available) */}
        {error.userDescription && (
          <div className="bg-blue-50 p-3 rounded-md border-l-4 border-blue-400">
            <p className="text-xs text-gray-600 font-semibold mb-1">
              User Description:
            </p>
            <p className="text-sm text-gray-800">{error.userDescription}</p>
          </div>
        )}

        {/* Footer with user info */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
          <UserIcon className="w-4 h-4 text-gray-500" />
          <span className="text-xs text-gray-600">
            {error.userSub ? (
              <span className="font-mono">{error.userSub}</span>
            ) : (
              <span className="italic">Anonymous</span>
            )}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default ErrorReportCard;
